import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Shield } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Roles() {
  const { can } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ modules: [], actions: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ key: "", name: "", permissions: {} });

  const load = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/roles/meta").then((r) => setMeta(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm({ key: "", name: "", permissions: {} }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ key: r.key, name: r.name, permissions: r.permissions || {} }); setOpen(true); };

  const toggle = (module, action) => {
    setForm((f) => {
      const cur = new Set(f.permissions[module] || []);
      cur.has(action) ? cur.delete(action) : cur.add(action);
      return { ...f, permissions: { ...f.permissions, [module]: [...cur] } };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/roles/${editing.id}`, { name: form.name, permissions: form.permissions });
      else await api.post("/roles", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Role Management" actions={can("roles", "create") && <Button size="sm" className="rounded-full" onClick={openNew} data-testid="role-add"><Plus className="mr-1 h-4 w-4" />Add Role</Button>}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {roles.map((r) => (
          <div key={r.id} className="rounded-xl border border-border bg-card p-5" data-testid={`role-card-${r.key}`}>
            <div className="flex items-center gap-3"><span className="grid h-10 w-10 place-items-center rounded-lg bg-accent text-accent-foreground"><Shield className="h-5 w-5" /></span><div><p className="font-display font-semibold">{r.name}</p><p className="text-xs text-muted-foreground">{r.key}{r.is_system && " · system"}</p></div></div>
            <p className="mt-3 text-sm text-muted-foreground">{Object.keys(r.permissions || {}).length} modules · {r.key === "super_admin" ? "Full access" : "Scoped"}</p>
            {can("roles", "edit") && r.key !== "super_admin" && <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => openEdit(r)} data-testid={`role-edit-${r.key}`}>Edit Permissions</Button>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Role Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" /></div>
              {!editing && <div><Label className="mb-1 block text-xs">Key (unique)</Label><Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s/g, "_") })} data-testid="role-key" /></div>}
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40"><tr><th className="px-3 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Module</th>{meta.actions.map((a) => <th key={a} className="px-2 py-2 text-center text-xs font-semibold capitalize text-muted-foreground">{a}</th>)}</tr></thead>
                <tbody className="divide-y divide-border">
                  {meta.modules.map((m) => (
                    <tr key={m}><td className="px-3 py-2 font-medium capitalize">{m.replace(/_/g, " ")}</td>
                      {meta.actions.map((a) => <td key={a} className="px-2 py-2 text-center"><Checkbox checked={(form.permissions[m] || []).includes(a)} onCheckedChange={() => toggle(m, a)} data-testid={`perm-${m}-${a}`} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button type="submit" className="rounded-full" data-testid="role-save">Save Role</Button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
